import type { WebSocketEventHandler } from '@directus/sdk';

/*
 * Works around @directus/sdk's realtime() composable: its async 'open' listener's returned
 * promise is never consumed, and the helper it awaits can reject with no accompanying socket
 * error/close - closing here forces that rejection through connect()'s own close listener so
 * connect() cannot hang forever. See gotchas.md.
 */
export class SafeAsyncWebSocket extends WebSocket {
  private wrapped = new WeakMap<WebSocketEventHandler, Map<string, EventListener>>();

  override addEventListener(
    type: string,
    listener: WebSocketEventHandler,
    options?: boolean | AddEventListenerOptions,
  ): void {
    const byType = this.wrapped.get(listener) ?? new Map<string, EventListener>();
    // Mirrors native addEventListener's (type, listener) no-op; capture isn't part of the key.
    if (byType.has(type)) return;

    const wrapped: EventListener = (event) => {
      const result = listener.call(this, event);
      if (result && typeof result.catch === 'function') {
        result.catch(() => this.close());
      }
    };
    byType.set(type, wrapped);
    this.wrapped.set(listener, byType);
    super.addEventListener(type, wrapped, options);
  }

  override removeEventListener(
    type: string,
    listener: WebSocketEventHandler,
    options?: boolean | EventListenerOptions,
  ): void {
    const byType = this.wrapped.get(listener);
    const wrapped = byType?.get(type);
    if (!byType || !wrapped) return;

    byType.delete(type);
    super.removeEventListener(type, wrapped, options);
  }
}
